import React from "react";
import styled from "styled-components";
import Icon from "../../Icons";
import moment from "moment";
import { REPETITION_PERIODS } from "../../../constants";
import { color, radius, fontFamily } from "../../Theme";

const Bar = styled.div`
  width: 100%;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: ${(p) => (p.$hideViews ? "center" : "space-between")};
  gap: 12px;
  padding: 12px 0;
`;

const Segmented = styled.div`
  display: inline-flex;
  padding: 3px;
  gap: 2px;
  border: 1px solid ${() => color("border-input")};
  border-radius: ${() => radius("control")}px;
  background: ${() => color("surface")};
`;

const SegmentButton = styled.button`
  min-width: 64px;
  height: 32px;
  padding: 0 16px;
  border: 0;
  border-radius: ${() => Math.max(radius("control") - 3, 4)}px;
  font-family: ${() => fontFamily()};
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  color: ${(p) => (p.$active ? color("surface") : color("text"))};
  background: ${(p) => (p.$active ? color("primary") : "transparent")};
  &:hover {
    background: ${(p) => (p.$active ? color("primary") : color("list"))};
  }
  &:focus-visible {
    outline: 2px solid ${() => color("primary")};
    outline-offset: 2px;
  }
`;

const Stepper = styled.div`
  display: inline-flex;
  align-items: center;
  height: 40px;
  border: 1px solid ${() => color("border-input")};
  border-radius: ${() => radius("control")}px;
  background: ${() => color("surface")};
  overflow: hidden;
`;

const StepButton = styled.button`
  width: 40px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  background: transparent;
  color: ${() => color("text")};
  cursor: pointer;
  &:hover {
    background: ${() => color("list")};
  }
  &:focus-visible {
    outline: 2px solid ${() => color("primary")};
    outline-offset: -2px;
  }
`;

const DateLabel = styled.div`
  min-width: 170px;
  padding: 0 12px;
  text-align: center;
  font-family: ${() => fontFamily()};
  font-size: 14px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  color: ${() => color("text")};
  border-left: 1px solid ${() => color("sched-line")};
  border-right: 1px solid ${() => color("sched-line")};
  line-height: 38px;
  white-space: nowrap;
`;

function viewLabel(view) {
  return view == REPETITION_PERIODS.DAY ? "Day" : view == REPETITION_PERIODS.WEEK ? "Week" : view == REPETITION_PERIODS.MONTH ? "Month" : "Year";
}

function CalendarControlls(props) {
  const { date, onPrev, onNext, onViewChanged, currentView, availableViews, hideViews, children } = props;

  let processedViews = [REPETITION_PERIODS.DAY];
  if (availableViews != null) {
    processedViews = [...availableViews];
  }

  const dateFormat =
    currentView == REPETITION_PERIODS.MONTH
      ? "MMMM YYYY"
      : currentView == REPETITION_PERIODS.DAY || currentView == REPETITION_PERIODS.WEEK
        ? "ddd DD MMMM YYYY"
        : "YYYY";

  return (
    <Bar $hideViews={hideViews}>
      {/* day and week segmented control */}
      {!hideViews && (
        <Segmented role="group" aria-label="Calendar view">
          {processedViews.map((view) => (
            <SegmentButton
              key={view}
              type="button"
              $active={currentView === view}
              aria-pressed={currentView === view}
              onClick={() => onViewChanged(view)}
            >
              {viewLabel(view)}
            </SegmentButton>
          ))}
        </Segmented>
      )}

      {/* date with backward and forward buttons */}
      <Stepper>
        <StepButton type="button" aria-label="Previous" onClick={() => onPrev()}>
          <Icon $icon={"ChevronLeft"} $size="24px" />
        </StepButton>
        <DateLabel>{moment(date).format(dateFormat)}</DateLabel>
        <StepButton type="button" aria-label="Next" onClick={() => onNext()}>
          <Icon $icon={"ChevronRight"} $size="24px" />
        </StepButton>
      </Stepper>

      {children}
    </Bar>
  );
}

export default CalendarControlls;
